import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { MoreHorizontal, Copy, UserX, Mail, Merge, Trash2 } from 'lucide-react';

interface UserData {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  department: string;
  is_active: boolean;
  last_active: string | null;
  scored_30d: number;
  avg_score_30d: number;
  fsr: number;
}

interface UserActionsDropdownProps {
  user: UserData;
  allUsers: UserData[];
  onRefresh: () => void;
}

interface MergePreview {
  submissions: number;
  scores: number;
  snapshots: number;
  points: number;
  checkins: number;
  favorites: number;
  conversations: number;
}

export function UserActionsDropdown({ user, allUsers, onRefresh }: UserActionsDropdownProps) {
  // Toggle active state
  const [isToggling, setIsToggling] = useState(false);

  // Delete user state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Resend invite state
  const [resendOpen, setResendOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ magic_link: string; email_template: string } | null>(null);

  // Merge user state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [targetUserId, setTargetUserId] = useState<string>('');
  const [mergePreview, setMergePreview] = useState<MergePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeStep, setMergeStep] = useState<'select' | 'confirm'>('select');

  const otherUsers = allUsers.filter(u => u.user_id !== user.user_id);

  const handleToggleActive = async () => {
    setIsToggling(true);
    try {
      const { error } = await supabase.functions.invoke('admin-update-user', {
        body: {
          user_id: user.user_id,
          is_active: !user.is_active,
        },
      });
      if (error) throw error;
      toast.success(user.is_active ? 'User deactivated' : 'User activated');
      onRefresh();
    } catch (error) {
      console.error('Error toggling user:', error);
      toast.error('Failed to update user');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: { user_id: user.user_id },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      
      toast.success('User deleted permanently');
      setDeleteOpen(false);
      onRefresh();
    } catch (error: unknown) {
      console.error('Error deleting user:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvite = async () => {
    setIsResending(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-resend-invite', {
        body: { user_id: user.user_id },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      
      setInviteResult({
        magic_link: data.magic_link,
        email_template: data.email_template,
      });
      toast.success('New invite link generated');
    } catch (error: unknown) {
      console.error('Error resending invite:', error);
      const message = error instanceof Error ? error.message : 'Failed to generate invite';
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  const closeResendModal = () => {
    setResendOpen(false);
    setInviteResult(null);
  };

  const handlePreviewMerge = async () => {
    if (!targetUserId) return;
    
    setIsLoadingPreview(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-merge-users', {
        body: {
          source_user_id: user.user_id,
          target_user_id: targetUserId,
          preview_only: true,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      
      setMergePreview(data.preview);
      setMergeStep('confirm');
    } catch (error: unknown) {
      console.error('Error previewing merge:', error);
      const message = error instanceof Error ? error.message : 'Failed to preview merge';
      toast.error(message);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleMerge = async () => {
    if (!targetUserId) return;
    
    setIsMerging(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-merge-users', {
        body: {
          source_user_id: user.user_id,
          target_user_id: targetUserId,
          preview_only: false,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      
      toast.success('Accounts merged successfully');
      closeMergeModal();
      onRefresh();
    } catch (error: unknown) {
      console.error('Error merging users:', error);
      const message = error instanceof Error ? error.message : 'Failed to merge accounts';
      toast.error(message);
    } finally {
      setIsMerging(false);
    }
  };

  const closeMergeModal = () => {
    setMergeOpen(false);
    setTargetUserId('');
    setMergePreview(null);
    setMergeStep('select');
  };

  const targetUser = otherUsers.find(u => u.user_id === targetUserId);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleToggleActive} disabled={isToggling}>
            <UserX className="h-4 w-4 mr-2" />
            {user.is_active ? 'Deactivate' : 'Activate'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setResendOpen(true)}>
            <Mail className="h-4 w-4 mr-2" />
            Resend Invite
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMergeOpen(true)} disabled={otherUsers.length === 0}>
            <Merge className="h-4 w-4 mr-2" />
            Merge Into...
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={() => setDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete User
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Permanently?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will permanently delete <strong>{user.email}</strong> and all their data including:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>All prompt submissions and scores</li>
                <li>Points and check-in history</li>
                <li>Profile and organization membership</li>
              </ul>
              <p className="text-destructive font-medium">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resend Invite Dialog */}
      <Dialog open={resendOpen} onOpenChange={closeResendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend Invite to {user.email}</DialogTitle>
            <DialogDescription>
              Generate a new magic link for this user to complete their onboarding.
            </DialogDescription>
          </DialogHeader>
          
          {!inviteResult ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                This will generate a new magic link and reset their onboarding status. 
                They'll need to complete onboarding again.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={closeResendModal}>Cancel</Button>
                <Button onClick={handleResendInvite} disabled={isResending}>
                  {isResending ? 'Generating...' : 'Generate New Link'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm text-green-700 dark:text-green-300">
                ✓ New invite link generated!
              </div>
              <div className="space-y-2">
                <Label>Magic Link</Label>
                <Input value={inviteResult.magic_link} readOnly className="font-mono text-xs" />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteResult.magic_link);
                    toast.success('Link copied!');
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
              </div>
              <div className="space-y-2">
                <Label>Email Template</Label>
                <Textarea value={inviteResult.email_template} readOnly rows={6} className="font-mono text-xs" />
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteResult.email_template);
                    toast.success('Email copied!');
                  }}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Email
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={closeResendModal}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Merge User Dialog */}
      <Dialog open={mergeOpen} onOpenChange={closeMergeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {mergeStep === 'select' ? 'Merge Account Into Another User' : 'Confirm Merge'}
            </DialogTitle>
            <DialogDescription>
              {mergeStep === 'select' 
                ? `Transfer all data from ${user.email} to another user account.`
                : `Review what will be transferred before confirming.`
              }
            </DialogDescription>
          </DialogHeader>

          {mergeStep === 'select' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Source Account (will be deleted)</Label>
                <Input value={user.email} readOnly className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Target Account (will receive data)</Label>
                <Select value={targetUserId} onValueChange={setTargetUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select target user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {otherUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.full_name || u.email} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeMergeModal}>Cancel</Button>
                <Button onClick={handlePreviewMerge} disabled={!targetUserId || isLoadingPreview}>
                  {isLoadingPreview ? 'Loading...' : 'Preview Merge'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm">
                <p className="font-medium text-yellow-700 dark:text-yellow-300 mb-2">
                  Data to be transferred to {targetUser?.email}:
                </p>
                <ul className="space-y-1 text-muted-foreground">
                  <li>• {mergePreview?.submissions || 0} submissions</li>
                  <li>• {mergePreview?.scores || 0} scores</li>
                  <li>• {mergePreview?.points || 0} point records</li>
                  <li>• {mergePreview?.checkins || 0} check-ins</li>
                  <li>• {mergePreview?.favorites || 0} favorites</li>
                  <li>• {mergePreview?.conversations || 0} builder conversations</li>
                </ul>
              </div>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
                <strong>{user.email}</strong> will be permanently deleted after merge.
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setMergeStep('select')}>Back</Button>
                <Button 
                  onClick={handleMerge} 
                  disabled={isMerging}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isMerging ? 'Merging...' : 'Confirm Merge'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
